import clsx from 'clsx';
import React, { ComponentType, useMemo, useRef, useState } from 'react';
import Chip from '@material-ui/core/Chip';
import IconButton from '@material-ui/core/IconButton';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { TextField, useEventCallback } from '@eas/common-web';
import { TreeDisabledComponent as CommonTreeDisabledComponent } from '@components/form/tree/tree-disabled-component';
import { TreeObject } from '@models';
import { LocalTreePopup } from './local-tree-popup';
import { useLocalTreeSource } from './local-tree-source-hook';
import { useStyles } from './local-tree-styles';
import { LocalTreeNode } from './local-tree-types';
import { formatSelectedLocalTreeAncestors } from './local-tree-utils';

export interface LocalTreeProps<OPTION extends TreeObject> {
  disabled?: boolean;
  form?: string;
  onChange: (value: OPTION[] | null | undefined) => void;
  url: string;
  value: OPTION[] | null | undefined;
  DisabledComponent?: ComponentType<{
    value: TreeObject[];
    disabled?: boolean;
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
  }>;
}

export function shouldCloseLocalTreePopup(
  anchorElement: HTMLDivElement | null,
  eventTarget: EventTarget | null
) {
  return !anchorElement?.contains(eventTarget as Node | null);
}

export function LocalTree<OPTION extends TreeObject>({
  disabled,
  form,
  onChange,
  url,
  value,
  DisabledComponent = CommonTreeDisabledComponent,
}: LocalTreeProps<OPTION>) {
  const classes = useStyles();
  const anchorElRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [popupOpen, setPopupOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const hasSelectedItems = (value?.length ?? 0) > 0;

  const { error, isExpanded, loading, nodeMap, toggleExpanded, visibleRoots } =
    useLocalTreeSource<OPTION>({
      hasSelectedItems,
      popupOpen,
      searchQuery,
      url,
    });

  const selectedIds = useMemo(
    () => new Set((value ?? []).map((item) => item.id)),
    [value]
  );

  const sortedValue = useMemo(
    () => [...(value ?? [])].sort((a, b) => (a.label > b.label ? 1 : -1)),
    [value]
  );

  const handleOpen = useEventCallback(() => {
    if (!disabled) {
      setPopupOpen(true);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  });

  const handleClose = useEventCallback(
    (event?: React.MouseEvent<Document, MouseEvent>) => {
      if (
        event &&
        !shouldCloseLocalTreePopup(anchorElRef.current, event.target ?? null)
      ) {
        return;
      }

      setPopupOpen(false);
      setSearchQuery('');
    }
  );

  const handleSearchChange = useEventCallback((nextValue: string | null) => {
    setSearchQuery(nextValue ?? '');

    if (!popupOpen) {
      setPopupOpen(true);
    }
  });

  const handleSelect = useEventCallback((node: LocalTreeNode<OPTION>) => {
    if (selectedIds.has(node.id)) {
      const nextValue = (value ?? []).filter((item) => item.id !== node.id);
      onChange(nextValue.length > 0 ? nextValue : []);
      return;
    }

    onChange([
      ...(value ?? []),
      {
        id: node.value.id,
        label: node.value.label,
      } as OPTION,
    ]);
  });

  const handleDelete = useEventCallback((item: OPTION) => {
    const nextValue = (value ?? []).filter(
      (currentItem) => currentItem.id !== item.id
    );
    onChange(nextValue.length > 0 ? nextValue : []);
  });

  return (
    <div
      className={clsx('EasInput-root', classes.wrapper, {
        [classes.wrapperDisabled]: disabled,
      })}
    >
      {disabled ? (
        <DisabledComponent disabled={true} value={sortedValue} />
      ) : (
        <div>
          <div className={classes.input} onClick={handleOpen} ref={anchorElRef}>
            <TextField
              ref={inputRef as any}
              form={form}
              showClearButton={false}
              endAdornment={
                <IconButton
                  size="small"
                  classes={{
                    root: clsx(classes.icon, {
                      [classes.iconOpened]: popupOpen,
                    }),
                  }}
                >
                  <ArrowDropDownIcon fontSize="small" />
                </IconButton>
              }
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          {!popupOpen && sortedValue.length > 0 && (
            <div className={classes.chips}>
              {sortedValue.map((item) => {
                const ancestors = formatSelectedLocalTreeAncestors(
                  item,
                  nodeMap
                );

                return (
                  <div key={item.id} className={classes.chipWithAncestors}>
                    <Chip
                      label={item.label}
                      className={classes.chip}
                      variant="outlined"
                      size="small"
                      onDelete={() => handleDelete(item)}
                    />
                    {ancestors && (
                      <span className={classes.chipAncestors}>{ancestors}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {popupOpen && anchorElRef.current && (
        <LocalTreePopup
          anchorElRef={anchorElRef}
          disabled={disabled}
          error={error}
          isExpanded={isExpanded}
          loading={loading}
          onClickAway={handleClose}
          onDelete={handleDelete}
          onSelect={handleSelect}
          onToggle={toggleExpanded}
          selectedItems={sortedValue}
          selectedIds={selectedIds}
          getSelectedItemAncestors={(item) =>
            formatSelectedLocalTreeAncestors(item, nodeMap)
          }
          visibleRoots={visibleRoots}
        />
      )}
    </div>
  );
}
